import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  LogIn, 
  LayoutDashboard, 
  CheckCircle, 
  Award, 
  HelpCircle,
  Database,
  Terminal,
  Brain,
  Play,
  FileText,
  Clock,
  AlertCircle,
  Target
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const StudentHelp = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">Student Help Guide</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about using Neo Prism Academy
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Accordion type="single" collapsible className="space-y-4">
          
          {/* Getting Started */}
          <AccordionItem value="getting-started" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <LogIn className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Getting Started</h3>
                  <p className="text-sm text-muted-foreground">Login and account access</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">No Self-Registration</p>
                    <p className="text-sm text-muted-foreground">
                      Student accounts are created and managed by administrators. You cannot sign up on your own.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Login Process</p>
                    <p className="text-sm text-muted-foreground">
                      Go to <Link to="/login" className="text-primary hover:underline">/login</Link> and enter the credentials provided by your administrator.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Account Approval</p>
                    <p className="text-sm text-muted-foreground">
                      Some accounts may require administrator approval before you can access courses.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Forgot Password?</p>
                    <p className="text-sm text-muted-foreground">
                      Use the "Forgot password?" link on the login page to reset your password via email.
                    </p>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Dashboard Navigation */}
          <AccordionItem value="dashboard" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <LayoutDashboard className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Your Dashboard</h3>
                  <p className="text-sm text-muted-foreground">Navigate your learning journey</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  After logging in, you'll be redirected to your <Link to="/dashboard" className="text-primary hover:underline">Dashboard</Link>.
                </p>
                <div className="grid gap-3">
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium">📚 Assigned Courses</p>
                      <p className="text-sm text-muted-foreground">View courses assigned to you by administrators. Only assigned courses are accessible.</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium">📊 Progress Tracking</p>
                      <p className="text-sm text-muted-foreground">See your completion percentage, modules finished, and quiz scores.</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium">🔥 Learning Streak</p>
                      <p className="text-sm text-muted-foreground">Track your daily learning streak and maintain consistency.</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium">🏆 Certificates Earned</p>
                      <p className="text-sm text-muted-foreground">Quick access to your earned certificates.</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Learning a Module */}
          <AccordionItem value="modules" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <BookOpen className="w-5 h-5 text-purple-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Learning a Module</h3>
                  <p className="text-sm text-muted-foreground">Understanding the 4 module tabs</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Each module contains 4 tabs to guide your learning:
                </p>
                <div className="grid gap-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">1</Badge>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <Play className="w-4 h-4" /> Learn
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Main content with text, code examples, and audio narration. Audio only plays in this tab.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">2</Badge>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <Terminal className="w-4 h-4" /> Labs
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Hands-on practice exercises with real code execution.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">3</Badge>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> Quiz
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Test your knowledge. Must pass with 85% or higher.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">4</Badge>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Resources
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Additional materials, documentation, and references.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 border mt-4">
                  <p className="font-medium mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4 text-emerald-500" /> Course Certificates
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Certificates are earned at the <strong>course level</strong> after completing all modules and quizzes. 
                    View your certificate eligibility on the Course Detail page or visit the{' '}
                    <Link to="/certificates" className="text-primary hover:underline">Certificates page</Link>.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Quiz Requirements */}
          <AccordionItem value="quizzes" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Target className="w-5 h-5 text-amber-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Quiz Requirements</h3>
                  <p className="text-sm text-muted-foreground">Passing scores and attempts</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-amber-500/30 bg-amber-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="w-5 h-5 text-amber-500" />
                        Passing Score
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-amber-500">85%</p>
                      <p className="text-sm text-muted-foreground">Minimum score required to pass each quiz</p>
                    </CardContent>
                  </Card>
                  <Card className="border-blue-500/30 bg-blue-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-500" />
                        Maximum Attempts
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-blue-500">3</p>
                      <p className="text-sm text-muted-foreground">Attempts allowed per module quiz</p>
                    </CardContent>
                  </Card>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 border">
                  <p className="font-medium mb-2">💡 Tips for Success</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Review the Learn tab content thoroughly before attempting the quiz</li>
                    <li>• Complete the Labs for hands-on practice</li>
                    <li>• If you don't pass, review the material before your next attempt</li>
                  </ul>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Certificate Requirements */}
          <AccordionItem value="certificates" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Award className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Certificate Requirements</h3>
                  <p className="text-sm text-muted-foreground">How to earn your certificate</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  To earn a certificate for a course, you must meet ALL of the following requirements:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/30">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="font-medium">100% Module Completion</p>
                      <p className="text-sm text-muted-foreground">Complete all modules in the course</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/30">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="font-medium">85%+ on All Quizzes</p>
                      <p className="text-sm text-muted-foreground">Pass each individual module quiz with 85% or higher</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/30">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="font-medium">80%+ Course Average</p>
                      <p className="text-sm text-muted-foreground">Maintain an overall course average of 80% or higher</p>
                    </div>
                  </div>
                </div>
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    Once eligible, claim your certificate from:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">Course Detail Page</Badge>
                    <Badge variant="secondary">
                      <Link to="/certificates" className="hover:underline">Certificates page</Link>
                    </Badge>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Special Features */}
          <AccordionItem value="features" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <Brain className="w-5 h-5 text-cyan-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Special Features</h3>
                  <p className="text-sm text-muted-foreground">Additional learning tools</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="grid gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Brain className="w-5 h-5 text-cyan-500" />
                      AI Tutor
                    </CardTitle>
                    <CardDescription>Get personalized help with your learning</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/ai-tutor">Open AI Tutor →</Link>
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Database className="w-5 h-5 text-blue-500" />
                      SQL Playground
                    </CardTitle>
                    <CardDescription>Practice SQL queries with real databases</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/sql-playground">Open SQL Playground →</Link>
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Terminal className="w-5 h-5 text-emerald-500" />
                      Node.js Labs
                    </CardTitle>
                    <CardDescription>Run Node.js code directly in your browser</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/webcontainer-demo">Open Node.js Labs →</Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </AccordionContent>
          </AccordionItem>

        </Accordion>

        {/* Quick Links */}
        <div className="mt-12 p-6 rounded-lg bg-muted/30 border">
          <h3 className="font-semibold mb-4">Quick Links</h3>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" asChild>
              <Link to="/dashboard">Dashboard</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/certificates">My Certificates</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/notifications">Notifications</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings">Settings</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentHelp;
